import { Instant } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import useInstantCountdown from "../../hooks/useInstantCountdown";
import { CaregiverId, RaffleParticipationStatus } from "../../messages";
import Scaffold from "../Scaffold/Scaffold";
import RaffleHeader from "./components/RaffleHeader";
import RaffleScreenEnded from "./components/RaffleScreenEnded";
import RaffleScreenOngoing from "./components/RaffleScreenOngoing";
import RaffleScreenUpcoming from "./components/RaffleScreenUpcoming";
import { RaffleParticipant, RafflePreviousWinner, RafflePrize } from "./Raffle.types";
import { getRaffleStatus } from "./Raffle.utils";

type Props = {
  raffleEndsAt: Instant;
  previousWinners: RafflePreviousWinner[];
  participants: RaffleParticipant[];
  prize: RafflePrize;
  winnerCaregiverId: CaregiverId | null;
  tokens: number;
  shiftsPerToken: number;
  shiftsToNextToken: number;
  participationStatus: RaffleParticipationStatus;
  avatar: string | null;
  onRequestNavigate: (page: Page) => void;
  onTimeToChooseWinner: () => void;
  onClickGoBack: () => void;
};

export default function RaffleScreen(props: Props) {
  const { totalSecondsWithNegative: timeLeftToStartInSeconds } = useInstantCountdown({
    targetInstant: props.raffleEndsAt,
  });

  const raffleStatus = React.useMemo(
    () => getRaffleStatus(timeLeftToStartInSeconds),
    [timeLeftToStartInSeconds]
  );

  return (
    <Scaffold.Root style={{ background: "linear-gradient(to bottom, #fff, var(--tw-pink-200))" }}>
      <Scaffold.Header overlay={true}>
        <RaffleHeader
          onRequestNavigate={props.onRequestNavigate}
          onClickGoBack={props.onClickGoBack}
        />
      </Scaffold.Header>

      <Scaffold.Body>
        {(() => {
          switch (raffleStatus) {
            case "upcoming":
              return (
                <RaffleScreenUpcoming
                  prize={props.prize}
                  raffleEndsAt={props.raffleEndsAt}
                  participants={props.participants}
                  previousWinners={props.previousWinners}
                  shiftsPerToken={props.shiftsPerToken}
                  shiftsToNextToken={props.shiftsToNextToken}
                  participationStatus={props.participationStatus}
                  tokens={props.tokens}
                  avatar={props.avatar}
                />
              );

            case "ongoing":
              return (
                <RaffleScreenOngoing
                  raffleEndsAt={props.raffleEndsAt}
                  participants={props.participants}
                  winnerCaregiverId={props.winnerCaregiverId}
                  avatarUrl={props.avatar}
                  participationStatus={props.participationStatus}
                  tokens={props.tokens}
                  onTimeToChooseWinner={props.onTimeToChooseWinner}
                />
              );

            case "ended":
              return (
                <RaffleScreenEnded previousWinners={props.previousWinners} prize={props.prize} />
              );
          }
        })()}
      </Scaffold.Body>
    </Scaffold.Root>
  );
}
